export type ApiError = {
  status?: number;
  message: string;
  details?: unknown;
};

const API_BASE_URL = (import.meta as unknown as { env: { VITE_API_BASE_URL?: string } }).env
  ?.VITE_API_BASE_URL;

function getBaseUrl() {
  if (API_BASE_URL && API_BASE_URL.trim().length > 0) return API_BASE_URL.replace(/\/$/, "");
  // In production the nginx reverse proxy exposes /api on the same origin.
  // For local dev set VITE_API_BASE_URL=http://localhost:9000 in .env.local
  return "/api";
}

let accessToken: string | null = null;

export function setAccessToken(token: string | null) {
  accessToken = token;
}

export function getAccessToken() {
  return accessToken;
}

async function request<T>(
  path: string,
  options: globalThis.RequestInit & { parseJson?: boolean } = {}
): Promise<T> {
  const res = await fetch(`${getBaseUrl()}${path}`, {
    ...options,
    headers: {
      ...(options.headers ?? {}),
      ...(accessToken ? { Authorization: `Bearer ${accessToken}` } : {}),
    },
  });

  if (!res.ok) {
    let message = `Request failed with status ${res.status}`;
    let details: unknown = undefined;

    try {
      const ct = res.headers.get("content-type") ?? "";
      if (ct.includes("application/json")) {
        const body = await res.json();
        details = body;
        message = body?.detail ?? body?.message ?? message;
      } else {
        message = await res.text();
      }
    } catch {
      // ignore
    }

    const err: ApiError = { status: res.status, message, details };
    throw err;
  }

  const parseJson = options.parseJson ?? true;
  if (!parseJson) return undefined as T;

  // tolerate empty responses
  const ct = res.headers.get("content-type") ?? "";
  if (!ct.includes("application/json")) return (await res.text()) as unknown as T;
  return (await res.json()) as T;
}

export const api = {
  // Auth
  login: (payload: { email: string; password: string }) =>
    request<{ access_token: string; refresh_token: string }>("/auth/login", {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(payload),
    }),

  refresh: (payload: { refresh_token: string }) =>
    request<{ access_token: string }>("/auth/refresh", {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(payload),
    }),

  logout: () =>
    request<void>("/auth/logout", {
      method: "POST",
      // backend returns 204
      parseJson: false,
    }),

  me: () => request<{
    id: string;
    name: string;
    email: string;
    role: string;
    organizationId?: string;
    organization_name?: string;
    organizationName?: string;
    // backend model might differ; keep flexible
    [k: string]: unknown;
  }>("/auth/me", { method: "GET" }),

  // Dashboard
  dashboardSummary: () => request<any>("/dashboard/summary"),

  // Alerts
  alerts: (params?: Record<string, string | number | boolean | undefined>) => {
    // Backend expects: start_date/end_date (dates), risk_level, alert_status, page, per_page, region
    // Avoid sending unknown params that would trigger validation errors (422).
    const qs = params
      ? new URLSearchParams(
          Object.entries(params)
            .filter(([_, v]) => v !== undefined)
            .map(([k, v]) => [k, String(v)])
        )
      : null;
    return request<any>(qs ? `/alerts?${qs.toString()}` : "/alerts");
  },


  // Predictions
  predictions: (params?: Record<string, string | number | boolean | undefined>) => {
    const qs = params
      ? new URLSearchParams(Object.entries(params).map(([k, v]) => [k, String(v ?? "")] ))
      : null;
    return request<any>(qs ? `/predictions?${qs.toString()}` : "/predictions");
  },

  predictionDistrict: (districtId: string | number) =>
    request<any>(`/predictions/${districtId}`),

  // Resources CRUD
  resources: (params?: Record<string, string | number | boolean | undefined>) => {
    const qs = params
      ? new URLSearchParams(Object.entries(params).map(([k, v]) => [k, String(v ?? "")] ))
      : null;
    return request<any>(qs ? `/resources?${qs.toString()}` : "/resources");
  },

  createResource: (payload: any) =>
    request<any>(`/resources`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(payload),
    }),

  updateResource: (id: string | number, payload: any) =>
    request<any>(`/resources/${id}`, {
      method: "PUT",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(payload),
    }),

  deleteResource: (id: string | number) =>
    request<void>(`/resources/${id}`, {
      method: "DELETE",
      parseJson: false,
    }),

  organizations: (params?: Record<string, string | number | boolean | undefined>) => {
    const qs = params
      ? new URLSearchParams(Object.entries(params).map(([k, v]) => [k, String(v ?? "")] ))
      : null;
    return request<any>(qs ? `/organizations?${qs.toString()}` : "/organizations");
  },

  settings: (params?: Record<string, string | number | boolean | undefined>) => {
    const qs = params
      ? new URLSearchParams(Object.entries(params).map(([k, v]) => [k, String(v ?? "")] ))
      : null;
    return request<any>(qs ? `/settings?${qs.toString()}` : "/settings");
  },

  // Alert status update
  patchAlert: (id: string | number, payload: { status: string }) =>
    request<any>(`/alerts/${id}`, {
      method: "PATCH",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(payload),
    }),

  // Weather
  weather: (params?: Record<string, string | number | boolean | undefined>) => {
    const qs = params
      ? new URLSearchParams(Object.entries(params).map(([k, v]) => [k, String(v ?? "")]))
      : null;
    return request<any>(qs ? `/weather?${qs.toString()}` : "/weather");
  },

  // Malaria cases (historical)
  malariaCases: (params?: Record<string, string | number | boolean | undefined>) => {
    const qs = params
      ? new URLSearchParams(Object.entries(params).map(([k, v]) => [k, String(v ?? "")]))
      : null;
    return request<any>(qs ? `/malaria?${qs.toString()}` : "/malaria");
  },

  // Settings – health, thresholds, users
  settingsHealth: () => request<any>("/settings/health"),

  settingsThresholds: () => request<any>("/settings/thresholds"),

  patchSettingsThresholds: (payload: { alert_high_threshold_pct: number; alert_medium_threshold_pct: number }) =>
    request<any>("/settings/thresholds", {
      method: "PATCH",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(payload),
    }),

  settingsUsers: () => request<any>("/settings/users"),

  createSettingsUser: (payload: Record<string, unknown>) =>
    request<any>("/settings/users", {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(payload),
    }),

  patchSettingsUser: (id: string | number, payload: Record<string, unknown>) =>
    request<any>(`/settings/users/${id}`, {
      method: "PATCH",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(payload),
    }),

  deleteSettingsUser: (id: string | number) =>
    request<void>(`/settings/users/${id}`, {
      method: "DELETE",
      parseJson: false,
    }),

  // Organizations extended
  createOrganization: (payload: Record<string, unknown>) =>
    request<any>("/organizations", {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(payload),
    }),

  getOrganization: (id: string | number) => request<any>(`/organizations/${id}`),

  updateOrganization: (id: string | number, payload: Record<string, unknown>) =>
    request<any>(`/organizations/${id}`, {
      method: "PUT",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(payload),
    }),

  deleteOrganization: (id: string | number) =>
    request<void>(`/organizations/${id}`, {
      method: "DELETE",
      parseJson: false,
    }),

  // Districts
  districts: () => request<any[]>("/districts"),

  createDistrict: (payload: Record<string, unknown>) =>
    request<any>("/districts", {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(payload),
    }),

  updateDistrict: (id: string | number, payload: Record<string, unknown>) =>
    request<any>(`/districts/${id}`, {
      method: "PUT",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(payload),
    }),

  deleteDistrict: (id: string | number) =>
    request<void>(`/districts/${id}`, {
      method: "DELETE",
      parseJson: false,
    }),

  // Settings API keys
  settingsApiKeys: () => request<any[]>("/settings/api-keys"),

  // Malaria case CRUD
  createMalariaCase: (payload: Record<string, unknown>) =>
    request<any>("/malaria", {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(payload),
    }),

  updateMalariaCase: (id: string | number, payload: Record<string, unknown>) =>
    request<any>(`/malaria/${id}`, {
      method: "PUT",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(payload),
    }),

  deleteMalariaCase: (id: string | number) =>
    request<void>(`/malaria/${id}`, {
      method: "DELETE",
      parseJson: false,
    }),

  // Topography sync trigger for a district
  syncDistrictTopography: (id: string | number) =>
    request<any>(`/districts/${id}/sync-topography`, {
      method: "POST",
      parseJson: false,
    }),

  // Manually trigger a Celery background task (admin only)
  // task: "retrain" | "weather" | "topography"
  triggerTask: (task: string) =>
    request<{ task: string; status: string; task_id: string | null; detail: string | null }>(
      `/settings/trigger/${task}`,
      { method: "POST" },
    ),
};

